import {ApplicationUserGroup, applicationUserGroupSchema} from "./ApplicationUserGroup";
import {useTranslation} from "react-i18next";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {makeRequired} from "../../../shared/components/forms/formUtils";
import {RhfForm} from "../../../shared/components/forms/RhfForm";
import {FormHeader} from "../../../shared/components/forms/FormHeader";
import {Grid} from "@mui/material";
import {MuiRhfTextField} from "../../../shared/components/forms/MuiRhfTextField";
import {ApplicationUserGroupMembershipForm} from "./ApplicationUserGroupMembershipForm";
import {Application} from "../Application";
import {useMutation, useQueryClient} from "react-query";
import {createApplicationUserGroup, deleteApplicationUserGroup, updateApplicationUserGroup} from "./ApplicationUserGroupApi";
import {MutateOptions} from "react-query/types/core/types";
import {useAxios} from "../../../shared/technical/rest/AxiosProvider";
import {
  isCustomValidationErrorResponse,
  isSpringValidationErrorResponse,
  ValidationErrorResponse
} from "../../../shared/technical/rest/errorHandling";

export function ApplicationUserGroupForm(props: {
  application: Application,
  userGroup: ApplicationUserGroup,
  handleClose: () => void,
}) {
  const {application, handleClose} = props;
  const {t} = useTranslation();
  const queryClient = useQueryClient();
  const axios = useAxios();

  const [formReadonly, setFormReadonly] = useState<boolean>(!!props.userGroup.id);
  const [userGroup, setUserGroup] = useState<ApplicationUserGroup>(props.userGroup);
  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: userGroup,
    resolver: yupResolver(applicationUserGroupSchema(t))
  });
  const {reset, register, setError} = methods;

  const updateUserGroupMutation = useMutation((applicationUserGroup: ApplicationUserGroup) =>
    updateApplicationUserGroup(axios, application.id!!, applicationUserGroup));
  const createUserGroupMutation = useMutation((applicationUserGroup: ApplicationUserGroup) =>
    createApplicationUserGroup(axios, application.id!!, applicationUserGroup));
  const deleteUserGroupMutation = useMutation((applicationUserGroup: ApplicationUserGroup) =>
    deleteApplicationUserGroup(axios, application.id!!, applicationUserGroup.id!!));

  const mutateOptions: MutateOptions<any, any, ApplicationUserGroup> = useMemo(() => ({
    onSuccess: () => {
      queryClient.invalidateQueries('applicationUserGroups').then();
      handleClose();
    },
    onError: (errorResponse: any, variables: ApplicationUserGroup, context: unknown) => {
      console.info("Inside onError of react-query", errorResponse);
      if (isCustomValidationErrorResponse(errorResponse) || isSpringValidationErrorResponse(errorResponse)) {
        const validationResponse = errorResponse as ValidationErrorResponse;
        validationResponse.data.fieldErrors.forEach((fieldError) => {
          console.info("Setting error on field " + fieldError.field);
          setError(fieldError.field as any, {message: t("FormErrors." + fieldError.defaultMessage, {field: fieldError.field})})
        });
      }
      throw errorResponse;
    }
  }), [queryClient, handleClose, setError, t]);

  const handleSave = useCallback(async (entity: ApplicationUserGroup) => {
    if (entity.id) {
      updateUserGroupMutation.mutate(entity, mutateOptions);
    } else {
      createUserGroupMutation.mutate(entity, mutateOptions);
    }
  }, [updateUserGroupMutation, createUserGroupMutation, mutateOptions]);

  const handleDelete = useCallback(async (entity: ApplicationUserGroup) => {
    if (!entity.id) {
      return
    }
    deleteUserGroupMutation.mutate(entity, mutateOptions);
  }, [deleteUserGroupMutation, mutateOptions]);

  useEffect(() => {
    register('id');
  }, [register]);

  useEffect(() => {
    setUserGroup(props.userGroup);
  }, [props.userGroup]);

  useEffect(() => {
    reset(userGroup);
  }, [userGroup, reset]);

  const stdFieldProps = {
    variant: "outlined" as "outlined",
    disabled: formReadonly,
    InputLabelProps: {
      shrink: true
    }
  }
  const required = makeRequired(applicationUserGroupSchema(t));
  const isNew = !(!!userGroup.id)
  const editFormTitle = isNew ? t('ApplicationUserGroup.titleNew') : t('ApplicationUserGroup.titleEdit') + ': ' + userGroup.name;
  return (
    <FormProvider {...methods} >
      <RhfForm id={'userGroup-form'} onSubmit={handleSave}>
        <FormHeader title={editFormTitle}
                    onCancel={handleClose}
                    onDelete={() => handleDelete(props.userGroup)}
                    canBeEdited={true}
                    readonly={formReadonly}
                    onStartEditing={() => setFormReadonly(false)}
        />
        <Grid container={true} sx={{margin: 2, width: 'auto'}}>
          <Grid item={true} xs={12} md={7} lg={8}>
            <Grid container={true} spacing={3}>
              <Grid item={true} xs={12}>
                <MuiRhfTextField name={'name'}
                                 label={t('ApplicationUserGroup.name')}
                                 fullWidth={true}
                                 required={required?.name}
                                 {...stdFieldProps}
                />
              </Grid>
            </Grid>
          </Grid>
          {props.userGroup.id &&
            <Grid item={true} xs={12} sx={{marginTop: 3}}>
              <ApplicationUserGroupMembershipForm application={application} applicationUserGroup={props.userGroup}
                                                  readonly={formReadonly}/>
            </Grid>
          }
        </Grid>
      </RhfForm>
    </FormProvider>
  );
}
